import { useMemo } from "react";

import type { Branding } from "@lagoon/config/branding";
import { connectionLabel, isStaffConnection, signInCopy } from "@lagoon/config/connections";
import { buttonVariants, LagoonLockup, SplashLayout, Text } from "@lagoon/ui";

import { authErrorMessage, readAuthError } from "../auth-errors.js";
import { Notice } from "../components/feedback.js";

/**
 * Owner sign-in.
 *
 * One button, where the passcode flow was two screens and a good deal of state.
 * All of that now happens between the platform and Auth0, and the browser only
 * ever sees a redirect and, on the way back, a cookie.
 *
 * `SplashLayout` is the same single-panel page the visitor passcode gate uses,
 * and that is the point of taking it from the library rather than styling a
 * card here: the gate is server rendered under a strict CSP and draws itself
 * with `.lg-splash` from the same stylesheet, so somebody who has just been
 * handed a passcode cannot tell that two different technologies drew the two
 * pages.
 *
 * LGN-169 turns the one button into a choice, because a customer-facing tenant
 * has more than one connection. The choice is drawn *here*, and each link names
 * its connection, so Auth0's own picker is never reached: that page is
 * tenant-wide on a shared Auth0 tenant, so it would show another product's
 * branding for a decision this page has already presented.
 *
 * Links rather than buttons with handlers. `/auth/login` is a server route, so
 * a link is what this actually is: inspectable, openable in a new tab, and
 * middle-clickable. `buttonVariants` is the same manoeuvre `invitation.tsx`
 * makes for its own sign-in link, so an anchor and a `Button` draw identically.
 *
 * LGN-179 fixes the words around the buttons. They were written when the only
 * connection was the staff one and said so, which is the wrong sentence to put
 * in front of a customer choosing between Microsoft, Google and email. The
 * customer copy comes from `@lagoon/config/connections`, which the connector's
 * own picker reads as well, because the two are the same decision on two
 * surfaces and a customer meets both.
 */

/**
 * The copy for the case where there is nothing to choose.
 *
 * Kept verbatim, and kept here rather than in the shared table, because it is
 * what dev2 shows until wave 3 of `PLAN_lagoon_pearldiver_auth0.md`: one staff
 * connection, one button, and a sentence that is true of exactly that. The
 * shared customer copy explains a choice, so putting it on a page with one
 * button would explain something that is not being asked.
 */
const SINGLE = {
  title: "Sign in",
  description: "Private sites, shared with the people you choose.",
  footer:
    "Owners sign in with their Black Pearl account. People you invite to read a site do not need one, and get a code by email instead.",
} as const;

export function SignIn(props: {
  branding: Branding;
  /**
   * The connections the platform says this deployment offers, in the order it
   * listed them. Empty is the normal case wherever there is one connection:
   * the screen then draws exactly the button it always drew, and `/auth/login`
   * sends no `connection` at all.
   */
  connections: readonly string[];
}): React.ReactElement {
  // Read once, from the address bar the server redirected to. Only a known
  // reason maps to a message, so nothing from the URL is ever rendered. The
  // table lives in `auth-errors.ts` because the signed-in shell reads the same
  // codes, and an unknown one now falls back to a neutral sentence rather than
  // to the "cannot sign in here" refusal (LGN-126).
  const message = authErrorMessage(readAuthError(window.location.search));

  /**
   * Where the round trip comes back to, captured once on render.
   *
   * Read at render rather than at click time because this screen is the whole
   * of what the shell shows a signed-out visitor: there is nothing on it that
   * changes the address bar, so the two moments are the same moment. The value
   * goes into the handshake cookie server side and never reaches Auth0.
   */
  const returnTo = useMemo(() => {
    const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    return here === "/" ? "" : here;
  }, []);

  /**
   * Customer providers first, the staff door last and smaller.
   *
   * Staff sign-in is a valid way in for the few people who need it and noise
   * for everybody else, so it is not offered as a peer of the provider the
   * customer actually has an account with.
   */
  const providers = useMemo(
    () => props.connections.filter((name) => !isStaffConnection(name)),
    [props.connections],
  );
  const staff = useMemo(
    () => props.connections.filter((name) => isStaffConnection(name)),
    [props.connections],
  );

  /**
   * The single-button case, unchanged.
   *
   * Nothing is configured, or one connection is: in dev today that is the one
   * staff connection, and this screen has to look exactly as it did. The only
   * difference is that a configured name is now carried through to Auth0. A
   * lone *customer* connection is labelled for what it actually asks for,
   * because "sign in with Black Pearl" would be a lie about a Google account.
   */
  if (props.connections.length <= 1) {
    const only = props.connections[0];
    const label =
      only === undefined || isStaffConnection(only)
        ? "Sign in with Black Pearl"
        : connectionLabel(only);

    return (
      <Layout branding={props.branding} message={message} copy={SINGLE}>
        <a className={buttonVariants({ block: true })} href={loginHref(returnTo, only)}>
          {label}
        </a>
      </Layout>
    );
  }

  const copy = signInCopy(props.branding);

  return (
    <Layout
      branding={props.branding}
      message={message}
      copy={{ title: copy.title, description: copy.choose, footer: copy.footer }}
    >
      {providers.map((name) => (
        <a key={name} className={buttonVariants({ block: true })} href={loginHref(returnTo, name)}>
          {connectionLabel(name)}
        </a>
      ))}

      {staff.length === 0 ? null : (
        <div className="lg:flex lg:flex-col lg:items-center lg:gap-2">
          {/*
            One line, above the link and in the same small tier, so somebody
            who does not work here reads why the link exists before deciding
            whether it is for them. The connector's picker says it too, in the
            same words, from the same table.
          */}
          <Text as="span" size="caption" tone="subtle">
            {copy.staffNote}
          </Text>
          {staff.map((name) => (
            <a
              key={name}
              className={buttonVariants({ variant: "link", size: "sm" })}
              href={loginHref(returnTo, name)}
            >
              {connectionLabel(name)}
            </a>
          ))}
        </div>
      )}
    </Layout>
  );
}

/**
 * The panel, so the two renderings above cannot drift apart.
 *
 * Both draw the same splash and the same error notice. What differs is the
 * controls inside and the three sentences around them: a page offering one
 * button is not asking the reader anything, and a page offering three is.
 */
function Layout(props: {
  branding: Branding;
  message: string | null;
  copy: { readonly title: string; readonly description: string; readonly footer: string };
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <SplashLayout
      brand={<LagoonLockup product={props.branding.productName} />}
      title={props.copy.title}
      description={props.copy.description}
      footer={props.copy.footer}
    >
      <div className="lg:flex lg:flex-col lg:gap-5">
        {props.message === null ? null : <Notice tone="danger">{props.message}</Notice>}
        {props.children}
      </div>
    </SplashLayout>
  );
}

/**
 * `/auth/login`, with only the parameters that have a value.
 *
 * Built by hand rather than with `URLSearchParams` so that the no-connection,
 * no-returnTo case is the bare path this screen has always linked to. The
 * platform allowlists `connection` against its own configuration, so a name
 * that outlived a configuration change costs a redirect to Auth0's picker
 * rather than an error.
 */
function loginHref(returnTo: string, connection: string | undefined): string {
  const params: string[] = [];
  if (returnTo !== "") params.push(`returnTo=${encodeURIComponent(returnTo)}`);
  if (connection !== undefined) params.push(`connection=${encodeURIComponent(connection)}`);
  return params.length === 0 ? "/auth/login" : `/auth/login?${params.join("&")}`;
}
